import { useEffect, useRef, useState } from "react"
import KanbanBoard from "../../../components/Kanban/Kanban";
import { useAuth } from "../../../contexts/AuthProvider/useAuth";
import { ButtonCard, formatDateTime, formatMonetary, smartPeriod } from "../../../commons/utils";
import { FiltroRequisicoes } from "./Filtro";
import { Paperclip, Pencil, Printer, Redo, Undo, X } from "lucide-react";
import { Gallery, GalleryHandler } from "../../Gallery";
import { deleteFileRequisicao, getCampos, getFilesRequisicao, getStatus, getUsuPrivilegio, hasPrivilegio, ModalRequisicao, ModalRequisicaoCamposPendentes, model_observacao, updateStatus, uploadFileRequisicao } from "./../utils";
import { ExCadastroPadrao } from "../../../components/ExCadastroPadrao";
import PdfGenerator, { createMenu, PdfGeneratorHandler } from "../../../components/PdfGenerator";

function createButton(hint: string, color: string, icon: JSX.Element, onClick: () => void, disabled?: boolean) {
    return (
        <ButtonCard hint={hint} color={color} iconcenter={icon} onClick={onClick} disabled={disabled} className='p-1 me-1' />
    );
}

export const RequisicoesKanban = () => {
    const auth = useAuth();

    const refKanbam = useRef<any>();
    const refFiltro = useRef<any>();
    const refgallery = useRef<GalleryHandler>(null);
    const refmodal = useRef<any>(null);
    const refrequisicao = useRef<any>(null);
    const refcampospendentes = useRef<any>(null);
    const refReportGenerator = useRef<PdfGeneratorHandler>(null)

    const [status, setStatus] = useState<any>();
    const [usuPrivilegios, setUsuPrivilegios] = useState<any>();
    const [campos, setCampos] = useState<any>();

    useEffect(() => {
        async function loadData() {
            const [privilegios, campos, status] = await Promise.all([
                getUsuPrivilegio(auth),
                getCampos(auth),
                getStatus(auth)
            ]);

            setUsuPrivilegios(privilegios);
            setCampos(campos);
            setStatus(status);
        }

        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function onGetData(data: any) {
        const newArray: any = [];
        refKanbam.current?.setTasks([]);

        const sequencias = status.map((item: any) => item.sequencia);

        const menorSequencia = Math.min(...sequencias);
        const maiorSequencia = Math.max(...sequencias);

        data.forEach((item: any) => {
            const { reqid, fornome, statid, imobdescricao, reqobservacao, impdescricao, reqnumero, impprioridade, reqdata, datastatus,
                total_requisitado, reqdesconto, usucodigo, statgerapdf, statsequencia, motivo, usunome, reqnumeronotafiscal, reqnumeropedidofornecedor } = item;

            const id = `${reqid} - ${usucodigo}`;
            const content =
                <div style={{ paddingTop: '5px' }}>
                    {imobdescricao && <div style={{ paddingBottom: '5px' }}>{imobdescricao}</div>}
                    {fornome && <div style={{ fontSize: '12px', paddingBottom: '7px' }}>{fornome}</div>}
                    {reqobservacao && <div style={{ fontSize: '12px', paddingBottom: '7px' }}>{reqobservacao}</div>}
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        {reqnumeronotafiscal && <div style={{ fontSize: '12px', paddingBottom: '7px' }}>NF: {reqnumeronotafiscal}</div>}
                        {reqnumeropedidofornecedor && <div style={{ fontSize: '12px', paddingBottom: '7px' }}>Pedido: {reqnumeropedidofornecedor}</div>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                        {reqdata && <div style={{ fontSize: '12px', paddingBottom: '7px' }}>{formatDateTime(reqdata)}</div>}
                        {((total_requisitado > 0) || (reqdesconto > 0)) &&
                            <div style={{ display: 'flex', justifyContent: 'flex-end', flex: 1 }}>
                                <div style={{ fontSize: '14px' }}>
                                    {reqdesconto ?
                                        <div style={{ fontSize: '12px', whiteSpace: 'nowrap' }}><span>{formatMonetary(total_requisitado) + ' - ' + formatMonetary(reqdesconto) + ' = '}</span>
                                            <span style={{ fontSize: '14px', textAlign: 'right' }}>{formatMonetary(total_requisitado - reqdesconto).toString()}</span>
                                        </div> :
                                        formatMonetary(total_requisitado)}
                                </div>
                            </div>
                        }
                    </div>
                    {motivo && <div style={{ fontSize: '12px', paddingBottom: '16px', paddingTop: '16px', color: 'var(--primary-color)', textAlign: 'center' }}>{motivo}</div>}
                    {usunome && <div style={{ fontSize: '12px', paddingBottom: '5px' }}>{usunome}</div>}
                </div>

            const columnId = statid;

            const details =
                <div>
                    <div>{impdescricao}</div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: impprioridade === 1 ? '#f15619' : 'none' }}>{reqnumero + (impprioridade === 1 ? ' - Urgente' : '')}</div>
                </div>

            const color = status.find((_status: any) => _status.value === statid)?.color;
            const privilegioPrior = hasPrivilegio(status, usuPrivilegios, campos, statid, false, total_requisitado);
            const privilegioNext = hasPrivilegio(status, usuPrivilegios, campos, statid, true, total_requisitado);

            const proximaSequencia = Math.min(
                ...status
                    .filter((s: any) => s.sequencia > statsequencia)
                    .map((s: any) => s.sequencia)
            );

            const proximosStatus = status.filter((s: any) => s.sequencia === proximaSequencia);

            const functions = (
                <>
                    <div style={{ flex: 1, fontSize: '11px', display: 'flex', alignItems: 'center' }}>{smartPeriod(datastatus)}</div>
                    {(statgerapdf === 1) && createMenu('Imprimir', color, <Printer size={20} />, (mode) => refReportGenerator.current?.generatePDF(mode, { reqid, usucodigo }))}

                    {(statsequencia !== menorSequencia) && (privilegioPrior.result || privilegioPrior.isValueLimit) &&
                        createButton(
                            'Retroceder Status',
                            color,
                            <Undo size={20} />,
                            () => refmodal.current.openModal({
                                statid: item.statid,
                                reqid: item.reqid,
                                usucodigo: item.usucodigo,
                                valorTotal: total_requisitado
                            }),
                            privilegioPrior.isValueLimit
                        )}

                    {proximosStatus.map((prox: any) => {
                        const isRejeicao = prox.rejeicao === 1;
                        const label = isRejeicao ? 'Rejeitar' : 'Avançar Status';
                        const icon = isRejeicao ? <X size={20} /> : <Redo size={20} />;
                        const colorBtn = isRejeicao ? '#d9534f' : color;

                        return (
                            (statsequencia !== maiorSequencia) &&
                            (privilegioNext.result || privilegioNext.isValueLimit) &&
                            createButton(
                                label,
                                colorBtn,
                                icon,
                                () =>
                                    updateStatus(
                                        auth,
                                        status,
                                        usuPrivilegios,
                                        campos,
                                        { ...item, rejeicao: prox.rejeicao },
                                        true,
                                        total_requisitado,
                                        (req, camposPendentes) =>
                                            refcampospendentes.current.openModal(req, camposPendentes)
                                    ).then(() => refFiltro.current.refresh()),
                                privilegioNext.isValueLimit
                            )
                        );
                    })}

                    {createButton('Editar', color, <Pencil size={20} />, () => refrequisicao.current.openModal(item))}
                    {createButton('Arquivos', color, <Paperclip size={20} />,
                        async () => {
                            const files = await getFilesRequisicao(auth, reqid, usucodigo);
                            refgallery.current?.openModal(files, '', reqid, true, usucodigo);
                        }
                    )}
                </>
            );

            const filterValues: string[] = [imobdescricao, fornome, reqobservacao, reqnumero, usunome, reqnumeronotafiscal, reqnumeropedidofornecedor]

            newArray.push({ id, content, columnId, details, undefined, functions, filterValues });
        });

        refKanbam.current?.setTasks(newArray);
    }

    return (
        status &&
        <>
            <KanbanBoard
                ref={refKanbam}
                columnsId={status?.map((item: any) => item.id)}
                status={status}
                filter={() => {
                    return <FiltroRequisicoes ref={refFiltro} onGetData={onGetData} showVencimento={campos.find((e: any) => e.reqcamponome === "reqdtvencimento")} />
                }}
            />
            <Gallery ref={refgallery} onUploadFile={(reqid, file, usucodigo) => uploadFileRequisicao(auth, reqid, file, usucodigo)} onRemoveFile={(movid, file) => deleteFileRequisicao(auth, movid, file)} />
            <ExCadastroPadrao ref={refmodal} model={model_observacao}
                success={(values: any) => {
                    updateStatus(auth, status, usuPrivilegios, campos, values, false, values.valorTotal)
                        .then(() => refFiltro.current.refresh())
                }} />
            <PdfGenerator ref={refReportGenerator} api="requisicaopdfgenerate" />
            <ModalRequisicao ref={refrequisicao} success={() => refFiltro.current.refresh(true)} />
            <ModalRequisicaoCamposPendentes
                ref={refcampospendentes}
                success={() => {
                    refFiltro.current.refresh(true);
                }}
            />
        </>
    )
}